/**
 * BuildIndicator — small always-visible chip showing the currently
 * running backend git SHA + how long ago it was committed. Serves
 * two purposes:
 *
 *  1. Lets the user visually confirm at a glance whether their
 *     latest deploy landed on production (no more "did you redeploy?"
 *     back-and-forth).
 *  2. Lets me — the coding agent — verify the same by curling
 *     `https://ytmaker.ai/api/version` on my end.
 *
 * Positioned as a fixed chip in the top-right corner. Click reveals
 * a tooltip with full commit hash + commit ISO timestamp + pod
 * startup time.
 */
import { useEffect, useState } from "react";
import { getBackendVersion } from "@/lib/api";

function fmtAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const sec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

export default function BuildIndicator() {
  const [info, setInfo] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const [, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const fetchInfo = () => {
      getBackendVersion()
        .then((d) => { if (!cancelled) setInfo(d); })
        .catch(() => { /* silent */ });
    };
    fetchInfo();
    // Re-poll every 60s so the chip refreshes after a redeploy
    // without needing a hard refresh.
    const idFetch = setInterval(fetchInfo, 60_000);
    // Re-render every 30s so the "Xm ago" label stays honest.
    const idTick = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => {
      cancelled = true;
      clearInterval(idFetch);
      clearInterval(idTick);
    };
  }, []);

  if (!info) return null;
  const build = info.build || {};
  // Prefer git SHA when the deploy image includes .git (preview
  // pods do; the Emergent production image doesn't). Otherwise fall
  // back to the pod's startup time so the chip still gives a
  // meaningful "am I on the latest?" signal — a fresh startup_iso
  // proves a fresh deploy.
  const timeIso = build.commit_iso || build.startup_iso;
  const ago = fmtAgo(timeIso);
  const timeKind = build.commit_iso ? "committed" : "deployed";

  return (
    <div
      className="fixed bottom-3 right-3 z-40"
      data-testid="build-indicator"
    >
      <button
        type="button"
        onClick={() => setExpanded((x) => !x)}
        className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-zinc-300 bg-black/70 backdrop-blur-sm border border-zinc-800 hover:border-zinc-700 px-2 py-1 rounded-sm transition-colors"
        title="Click for full build info"
      >
        <span className="text-zinc-600">{timeKind}</span>{" "}
        <span className="text-zinc-300">{ago || "?"}</span>
        {build.sha_short ? (
          <span className="text-zinc-600"> · {build.sha_short}</span>
        ) : null}
      </button>
      {expanded && (
        <div
          data-testid="build-indicator-details"
          className="absolute bottom-full right-0 mb-2 w-80 bg-zinc-950 border border-zinc-800 rounded-sm p-3 shadow-xl font-mono text-[10px] leading-relaxed"
        >
          <div className="text-zinc-500 uppercase tracking-[0.25em] mb-2 text-[9px]">
            build info
          </div>
          <Row label="commit sha" value={build.sha || "(unknown)"} />
          <Row label="committed" value={build.commit_iso || "(unknown)"} />
          <Row label="pod startup" value={build.startup_iso || "(unknown)"} />
          <Row label="pipeline" value={info.pipeline_version || "(unknown)"} />
          <div className="text-zinc-600 mt-2 text-[9px] normal-case tracking-normal">
            Green heartbeat + fresh sha = you&apos;re on the latest deploy.
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2 py-0.5 border-b border-zinc-900 last:border-0">
      <span className="text-zinc-600 uppercase tracking-widest w-20 shrink-0">
        {label}
      </span>
      <span className="text-zinc-300 break-all">{value}</span>
    </div>
  );
}
